import { useState, useEffect, useMemo } from 'react'
import { api } from '@/api/client'
import { teamMembersApi } from '@/api/client'
import { usePresalesDashboardStore } from '../store'
import { TASK_TEMPLATES } from '../data/taskTemplates'
import type { PresalesTask, PresalesTaskTemplate } from '@/api/types'
import type { ComboboxOption } from '@/components/ui/Combobox'

// ==================== Constants ====================

// Presales team members loaded from DB (cached per session)
let _presalesMembersCache: string[] | null = null

async function loadPresalesMembers(): Promise<string[]> {
  if (_presalesMembersCache) return _presalesMembersCache
  try {
    const res = await teamMembersApi.list('presales')
    if (res.success && res.data) {
      _presalesMembersCache = res.data.items.map(m => m.name)
      return _presalesMembersCache
    }
  } catch { /* fall through */ }
  return []
}

// Full presales member cache (name + email)
let _presalesMembersFullCache: { name: string; email: string }[] | null = null

async function loadPresalesMembersFull(): Promise<{ name: string; email: string }[]> {
  if (_presalesMembersFullCache) return _presalesMembersFullCache
  try {
    const res = await teamMembersApi.list('presales')
    if (res.success && res.data) {
      _presalesMembersFullCache = res.data.items.map(m => ({ name: m.name, email: m.email }))
      _presalesMembersCache = _presalesMembersFullCache.map(m => m.name)
      return _presalesMembersFullCache
    }
  } catch { /* fall through */ }
  return []
}

// Hook to get presales member names from DB. Returns [] until loaded.
export function usePresalesMembers(): string[] {
  const [members, setMembers] = useState<string[]>(_presalesMembersCache || [])

  useEffect(() => {
    loadPresalesMembersFull().then(m => setMembers(m.map(x => x.name)))
  }, [])

  return members
}

// Hook to get presales members with email. For components that need email (e.g., calendar filter).
export function usePresalesMembersFull(): { name: string; email: string }[] {
  const [members, setMembers] = useState<{ name: string; email: string }[]>(_presalesMembersFullCache || [])

  useEffect(() => {
    loadPresalesMembersFull().then(setMembers)
  }, [])

  return members
}

export const TEMPLATE_BADGE_COLORS: Record<string, string> = {
  create_v1_uat: 'bg-blue-900/30 text-blue-300 border-blue-700/40',
  create_v2_uat: 'bg-blue-900/30 text-blue-300 border-blue-700/40',
  send_v1_invite: 'bg-green-900/30 text-green-300 border-green-700/40',
  send_v2_invite: 'bg-green-900/30 text-green-300 border-green-700/40',
  portal_invite_sent: 'bg-purple-900/30 text-purple-300 border-purple-700/40',
  api_key_sent: 'bg-cyan-900/30 text-cyan-300 border-cyan-700/40',
  postman_collection_sent: 'bg-orange-900/30 text-orange-300 border-orange-700/40',
  test_guide_sent: 'bg-teal-900/30 text-teal-300 border-teal-700/40',
  answer_question: 'bg-amber-900/30 text-amber-300 border-amber-700/40',
  custom_work: 'bg-rose-900/30 text-rose-300 border-rose-700/40',
  setup: 'bg-indigo-900/30 text-indigo-300 border-indigo-700/40',
  other: 'bg-gray-800/30 text-gray-400 border-gray-600/40',
}

export const inputClasses =
  'w-full px-3 py-1.5 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-md text-[var(--text-primary)] text-sm placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'

// ==================== Helpers ====================

export function getTemplateBadgeLabel(template: string): string {
  const tmpl = TASK_TEMPLATES.find((t) => t.value === template)
  return tmpl?.label || template
}

export function isOverdue(task: PresalesTask): boolean {
  if (task.status === 'completed' || !task.due_date) return false
  return new Date(task.due_date) < new Date(new Date().toDateString())
}

export function handleTemplateChangeHelper(
  value: string,
  setTemplate: (v: PresalesTaskTemplate) => void,
  setDescription: (v: string) => void,
) {
  const tmpl = TASK_TEMPLATES.find((t) => t.value === value)
  setTemplate(value as PresalesTaskTemplate)
  if (tmpl && tmpl.defaultDescription) {
    setDescription(tmpl.defaultDescription)
  } else {
    setDescription('')
  }
}

export function formatDuration(days: number | null): string {
  if (days === null || days === undefined) return '—'
  if (days < 1) return '< 1 day'
  if (days === 1) return '1 day'
  return `${days} days`
}

// ==================== Shared Hooks ====================

/**
 * Derives team members from all deals in the store, supplemented by
 * presales team members from the database (via API).
 * Returns a sorted, deduplicated list with presales members first.
 */
export function useTeamMembers(): string[] {
  const { deals } = usePresalesDashboardStore()
  const [presalesMembers, setPresalesMembers] = useState<string[]>([])

  useEffect(() => {
    loadPresalesMembers().then(setPresalesMembers)
  }, [])

  return useMemo(() => {
    const members = new Set<string>()
    // Add all presales team members from DB
    presalesMembers.forEach(name => members.add(name))
    deals.forEach((d) => {
      if (d.presales_engineer) members.add(d.presales_engineer.trim())
      if (d.solutions_consultant_owner) members.add(d.solutions_consultant_owner.trim())
    })
    const arr = Array.from(members).filter((m) => m && isNaN(Number(m)))
    const deduped = arr.filter(
      (name) => !arr.some((other) => other !== name && other.toLowerCase().includes(name.toLowerCase())),
    )
    // Sort presales members first, then alphabetical
    return deduped.sort((a, b) => {
      const aPri = presalesMembers.indexOf(a)
      const bPri = presalesMembers.indexOf(b)
      if (aPri !== -1 && bPri !== -1) return aPri - bPri
      if (aPri !== -1) return -1
      if (bPri !== -1) return 1
      return a.localeCompare(b)
    })
  }, [deals, presalesMembers])
}

/**
 * Loads key person suggestions from API (product managers) + team members.
 * Returns ComboboxOption[] for the key person combobox.
 */
export function useKeyPersonOptions(teamMembers: string[]): ComboboxOption[] {
  const [knownKeyPeople, setKnownKeyPeople] = useState<string[]>([])

  useEffect(() => {
    const loadKeyPeople = async () => {
      try {
        const res = await api.productManagers.list()
        const pmNames = (res.data?.items || [])
          .filter((pm: any) => pm.is_active)
          .map((pm: any) => pm.name as string)
        const combined = new Set([...pmNames, ...teamMembers])
        const sorted = Array.from(combined).sort()
        const deduped = sorted.filter(
          (name) => !sorted.some((other) => other !== name && other.toLowerCase().includes(name.toLowerCase())),
        )
        setKnownKeyPeople(deduped)
      } catch {
        const fallback = [...teamMembers].sort()
        const deduped = fallback.filter(
          (name) => !fallback.some((other) => other !== name && other.toLowerCase().includes(name.toLowerCase())),
        )
        setKnownKeyPeople(deduped)
      }
    }
    loadKeyPeople()
  }, [teamMembers])

  return useMemo<ComboboxOption[]>(() => {
    return knownKeyPeople.map((name) => ({ value: name, label: name }))
  }, [knownKeyPeople])
}

/**
 * Converts team members array to ComboboxOption[].
 */
export function useTeamMemberOptions(teamMembers: string[]): ComboboxOption[] {
  return useMemo<ComboboxOption[]>(() => {
    return teamMembers.map((m) => ({ value: m, label: m }))
  }, [teamMembers])
}

/**
 * Returns task template options for combobox.
 */
export function useTaskTemplateOptions(): ComboboxOption[] {
  return useMemo<ComboboxOption[]>(() => {
    return TASK_TEMPLATES.map((t) => ({ value: t.value, label: t.label }))
  }, [])
}

// ==================== Types ====================

export interface TaskCreatePayload {
  template: PresalesTaskTemplate
  description: string
  assigned_to?: string
  due_date?: string
  jira_ticket?: string
  key_person?: string
  status_update?: string
  is_hard_deadline?: boolean
  is_critical_path?: boolean
}
